import { primitiveTokens } from "@appretio/ui-kit";
import type {
  AgCartesianAxisOptions,
  AgWaterfallSeriesOptions
} from "ag-charts-enterprise";
import { useMemo } from "react";
import type { PriceDilutionApiRow } from "../../../../pages/inSeasonReviewPage/hooks/useFetchPriceDilution";

type Row = {
  /**
   * KPI label
   */
  kpi: string;
  /**
   * Amount value (can be positive or negative)
   */
  amount?: number;
  /**
   * Initial KPI value for comparison
   */
  initialKpi?: number;
  /**
   * Current KPI value for comparison
   */
  currentKpi?: number;
};

const LABELS = {
  AUR_ASSORTMENT: "AUR Assortment",
  AUR_PURCHASE: "AUR Purchase",
  GS_I_AUR: "GS I AUR",
  GS_II_AUR: "GS II AUR",
  GR_II_AUR: "GR II AUR",
  NS_III_AUR: "NS III AUR"
} as const;

const AUR_UNIT = "AUR (EURO)";
const X_KEY = "kpi";
const Y_KEY = "amount";

/**
 * Safely divides two numbers, returning 0 if the denominator is zero or negative.
 */
function safeDivision(numerator: number, denominator: number): number {
  return denominator > 0 ? numerator / denominator : 0;
}

/**
 * Hook to generate AG Charts options for Price Dilution Waterfall chart.
 *
 * Calculates Average Unit Retail (AUR) at each stage of the sales lifecycle and
 * visualizes price changes as a waterfall progression:
 *
 * Assortment RRP → Purchase → GS I → GS II → GR II → NS III
 *
 * Each step shows the price delta (dilution) between consecutive stages, helping
 * identify where and how much pricing power is being lost through the sales cycle.
 */

export function usePriceDilutionOptions(data?: PriceDilutionApiRow[]) {
  return useMemo(() => {
    const axes: AgCartesianAxisOptions[] = [
      {
        type: "category",
        position: "bottom",
        label: {
          wrapping: "never",
          rotation: 0
        },
        title: { text: "" }
      },
      {
        type: "number",
        position: "left",
        title: { text: AUR_UNIT },
        label: { formatter: ({ value }) => `${value.toFixed(2)}` }
      }
    ];

    if (!data || data.length === 0)
      return { series: [] as AgWaterfallSeriesOptions[], axes };

    const totals = data.reduce(
      (accumulator, row) => {
        // Gross Sales phase I: revenue and units sold
        accumulator.gs1_sum += row.gs1_sum;
        accumulator.gis1_sum += row.gis1_sum;

        // Gross Sales phase II: revenue (using GS I units as denominator)
        accumulator.gs2_sum += row.gs2_sum;

        // Net Sales phase III: revenue and units sold
        accumulator.ns3_sum += row.ns3_sum;
        accumulator.nis3_sum += row.nis3_sum;

        // Gross Returns phase II: revenue and units returned
        accumulator.gr2_sum += row.gr2_sum;
        accumulator.gir_sum += row.gir_sum;

        // Purchase metrics: units purchased over time
        accumulator.time_purchase_sum += row.time_purchase_sum;

        // Assortment metrics: total retail value and size weighted by RRP
        accumulator.assortment_retail_value +=
          row.style_recommended_retail_price_eur * row.assortment_size;
        accumulator.assortment_size += row.assortment_size;

        // Purchase retail value: total value of purchased units at RRP
        accumulator.purchase_retail_value +=
          row.style_recommended_retail_price_eur * row.time_purchase_sum;

        return accumulator;
      },
      {
        gs1_sum: 0,
        gs2_sum: 0,
        gis1_sum: 0,
        ns3_sum: 0,
        nis3_sum: 0,
        gr2_sum: 0,
        gir_sum: 0,
        time_purchase_sum: 0,
        assortment_retail_value: 0,
        assortment_size: 0,
        purchase_retail_value: 0
      }
    );

    //  (absolute levels)
    const levels: { label: string; value: number }[] = [
      {
        label: LABELS.AUR_ASSORTMENT,
        value: safeDivision(
          totals.assortment_retail_value,
          totals.assortment_size
        )
      },
      {
        label: LABELS.AUR_PURCHASE,
        value: safeDivision(
          totals.purchase_retail_value,
          totals.time_purchase_sum
        )
      },
      totals.gis1_sum > 0
        ? {
            label: LABELS.GS_I_AUR,
            value: safeDivision(totals.gs1_sum, totals.gis1_sum)
          }
        : null,
      totals.gis1_sum > 0
        ? {
            label: LABELS.GS_II_AUR,
            value: safeDivision(totals.gs2_sum, totals.gis1_sum)
          }
        : null,
      totals.gir_sum > 0
        ? {
            label: LABELS.GR_II_AUR,
            value: safeDivision(totals.gr2_sum, totals.gir_sum)
          }
        : null,
      totals.nis3_sum > 0
        ? {
            label: LABELS.NS_III_AUR,
            value: safeDivision(totals.ns3_sum, totals.nis3_sum)
          }
        : null
    ].filter(Boolean) as { label: string; value: number }[];

    //["absolute"] + ["relative", "total"] × (levels.length − 1)

    const waterfallData: Row[] = [];
    const cumulativeTotalMarkers: {
      totalType: "total";
      index: number;
      axisLabel: string;
    }[] = [];

    // Initial level: absolute bar for level 0
    waterfallData.push({ kpi: levels[0].label, amount: levels[0].value });

    // Marker for cumulative total at level 0
    cumulativeTotalMarkers.push({
      totalType: "total",
      index: 0,
      axisLabel: levels[0].label
    });

    // Subsequent levels: relative deltas and total placeholders
    for (let i = 0; i < levels.length - 1; i++) {
      const priceDelta = levels[i + 1].value - levels[i].value;

      // relative (the delta from previous absolute):
      waterfallData.push({
        kpi: `Dilution ${i + 1}`,
        amount: priceDelta,
        initialKpi: levels[i].value,
        currentKpi: levels[i + 1].value
      });

      cumulativeTotalMarkers.push({
        totalType: "total",
        index: waterfallData.length - 1,
        axisLabel: levels[i + 1].label
      });
    }

    const series: AgWaterfallSeriesOptions[] = [
      {
        type: "waterfall",
        data: waterfallData,
        xKey: X_KEY,
        yKey: Y_KEY,
        cursor: "pointer",
        tooltip: {
          renderer: function ({ datum, xKey, yKey, itemId }) {
            return {
              heading: datum[xKey],
              title: itemId,
              data: [
                {
                  label: datum[xKey],
                  value: datum[yKey].toFixed(2)
                },
                ...(typeof datum.initialKpi === "number"
                  ? [
                      {
                        label: "Initial KPI:",
                        value: datum.initialKpi.toFixed(2)
                      }
                    ]
                  : []),
                ...(typeof datum.currentKpi === "number"
                  ? [
                      {
                        label: "Current KPI",
                        value: datum.currentKpi.toFixed(2)
                      }
                    ]
                  : [])
              ]
            };
          }
        },
        item: {
          positive: {
            fill: primitiveTokens.colors.graph.green[400],
            stroke: primitiveTokens.colors.graph.green[400]
          },
          negative: {
            fill: primitiveTokens.colors.graph.red[500],
            stroke: primitiveTokens.colors.graph.red[500]
          },
          total: {
            fill: primitiveTokens.colors.graph.blue[700],
            stroke: primitiveTokens.colors.graph.blue[700]
          }
        },
        totals: cumulativeTotalMarkers
      }
    ];

    return { series, axes };
  }, [data]);
}
